import { useState } from "react";
import { MessageCircle, Zap } from "lucide-react";
import { useLang } from "../i18n";
import { Reveal, Eyebrow } from "./common";
import { wa } from "../lib/site";

function Field({ label, options, value, onChange, testPrefix }) {
    return (
        <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-3">
                {label}
            </p>
            <div className="flex flex-wrap gap-2.5">
                {options.map((opt, idx) => (
                    <button
                        key={opt}
                        data-testid={`${testPrefix}-${idx}`}
                        onClick={() => onChange(idx)}
                        className={`px-4 py-2.5 rounded-full text-sm border transition-all ${
                            value === idx
                                ? "bg-volt text-obsidian border-volt font-bold"
                                : "border-white/15 text-slate-300 hover:border-volt/60 hover:text-white"
                        }`}
                    >
                        {opt}
                    </button>
                ))}
            </div>
        </div>
    );
}

export default function QuoteBuilder() {
    const { lang, t } = useLang();
    const [serviceIdx, setServiceIdx] = useState(0);
    const [propertyIdx, setPropertyIdx] = useState(0);
    const [regionIdx, setRegionIdx] = useState(0);

    const service = t.quote.serviceOptions[serviceIdx];
    const property = t.quote.propertyOptions[propertyIdx];
    const region = t.quote.regionOptions[regionIdx];

    const msg =
        lang === "pt"
            ? `Olá! Vim pelo site da ANG Engenharia.\n\nServiço: ${service}\nImóvel: ${property}\nRegião: ${region}\n\nGostaria de solicitar um orçamento.`
            : `Hi! I came from the ANG Engenharia website.\n\nService: ${service}\nProperty: ${property}\nRegion: ${region}\n\nI'd like to request a quote.`;

    return (
        <section id="orcamento" className="relative py-24 md:py-32">
            <div className="max-w-5xl mx-auto px-5 md:px-10">
                <Reveal className="mb-12 text-center max-w-2xl mx-auto">
                    <Eyebrow>{t.quote.eyebrow}</Eyebrow>
                    <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                        {t.quote.title}
                    </h2>
                    <p className="mt-4 text-slate-400">{t.quote.sub}</p>
                </Reveal>

                <Reveal delay={0.15}>
                    <div className="relative overflow-hidden rounded-3xl border border-volt/25 bg-surface/70 backdrop-blur p-7 md:p-12">
                        <div className="absolute -top-24 -left-24 w-80 h-80 bg-volt/10 blur-[100px] rounded-full pointer-events-none" />
                        <div className="relative grid md:grid-cols-3 gap-8">
                            <Field
                                label={t.quote.service}
                                options={t.quote.serviceOptions}
                                value={serviceIdx}
                                onChange={setServiceIdx}
                                testPrefix="calculator-service"
                            />
                            <Field
                                label={t.quote.property}
                                options={t.quote.propertyOptions}
                                value={propertyIdx}
                                onChange={setPropertyIdx}
                                testPrefix="calculator-property-type"
                            />
                            <Field
                                label={t.quote.region}
                                options={t.quote.regionOptions}
                                value={regionIdx}
                                onChange={setRegionIdx}
                                testPrefix="calculator-region"
                            />
                        </div>

                        <div className="relative mt-10 flex flex-col sm:flex-row items-center justify-between gap-5 pt-8 border-t border-white/10">
                            <p className="flex items-center gap-2.5 text-slate-400 font-mono text-xs">
                                <Zap className="w-4 h-4 text-volt shrink-0" />
                                {service} · {property} · {region}
                            </p>
                            <a
                                data-testid="calculator-submit-whatsapp"
                                href={wa(msg)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-3 w-full sm:w-auto bg-volt text-obsidian font-bold px-8 py-4 rounded-full hover:shadow-[0_0_40px_rgba(255,230,0,0.45)] hover:-translate-y-0.5 transition-all"
                            >
                                <MessageCircle className="w-5 h-5" />
                                {t.quote.btn}
                            </a>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
